import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ScanEye, Eye, Wrench, Brain, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import DetectionIntelligencePanel from "@/components/dashboard/DetectionIntelligencePanel";
import VisualImpactTab from "@/components/detection/VisualImpactTab";
import OperationalTab from "@/components/detection/OperationalTab";
import AIIntelligenceTab from "@/components/detection/AIIntelligenceTab";

const POLL_MS = 5000;

const TABS = [
  { key: "overview",    label: "Overview",            icon: ScanEye, enabled: true,  hint: "Real-time AI vision classifications" },
  { key: "visual",      label: "High Visual Impact",  icon: Eye,     enabled: true,  hint: "Crowd heatmaps · Intrusion · Fight · Fire-Smoke · Congestion alerts" },
  { key: "operational", label: "Operational",         icon: Wrench,  enabled: true,  hint: "Queue analytics · Parking · Emergency routes · Exit monitoring" },
  { key: "ai",          label: "AI Intelligence",     icon: Brain,   enabled: true,  hint: "Behavioral anomaly · Panic prediction · Suspicious activity scoring · Predictive congestion" },
];

export default function DetectionPage() {
  const { bootstrap } = useOutletContext();
  const navigate = useNavigate();
  const zones = bootstrap?.zones || [];

  const [timeRange, setTimeRange] = useState("1h");
  const [zoneId, setZoneId] = useState(null);
  const [panels, setPanels] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [parking, setParking] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [data, hm, park] = await Promise.all([
        api.dashboardPanels({
          time_range: timeRange,
          ...(zoneId ? { zone_id: zoneId } : {}),
        }),
        api.heatmap().catch(() => ({ zones: [] })),
        api.parkingSnapshot().catch(() => ({ lots: [] })),
      ]);
      setPanels(data);
      setHeatmap(hm.zones || []);
      setParking(park.lots || []);
    } catch (_e) {
      // keep last good data
    } finally {
      setRefreshing(false);
    }
  }, [timeRange, zoneId]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="page-detection">
      {/* Title row */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-border bg-[hsl(var(--surface))] shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display font-black text-sm leading-none">Detection Intelligence</h1>
          <span className="micro-label hidden sm:inline">AI Vision · Behavioral Analytics · Predictive Risk</span>
        </div>
        <DashboardFilters
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          zoneId={zoneId}
          onZoneChange={setZoneId}
          zones={zones}
          onRefresh={load}
          refreshing={refreshing}
        />
      </div>

      <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <TabsList className="px-2 pt-2 pb-0 bg-transparent border-b border-border h-auto rounded-none justify-start gap-1 shrink-0 flex-wrap">
          {TABS.map(({ key, label, icon: Icon, enabled, hint }) => (
            <TabsTrigger
              key={key}
              value={key}
              disabled={!enabled}
              title={hint}
              data-testid={`detection-tab-${key}`}
              className="rounded-none border border-border border-b-0 data-[state=active]:bg-[hsl(var(--surface))] data-[state=active]:border-b-[hsl(var(--surface))] data-[state=active]:!shadow-none px-3 py-1.5 text-[11px] font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {!enabled && <Lock className="w-3 h-3 ml-0.5 text-muted-foreground" />}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3 sm:p-4">
            <DetectionIntelligencePanel data={panels?.detection} />
          </div>
        </TabsContent>

        <TabsContent value="visual" className="flex-1 m-0 overflow-y-auto">
          <VisualImpactTab
            visualImpact={panels?.detection?.visual_impact}
            heatmap={heatmap}
            onOpenIncident={() => navigate("/incidents")}
          />
        </TabsContent>

        <TabsContent value="operational" className="flex-1 m-0 overflow-y-auto">
          <OperationalTab
            operational={panels?.detection?.operational}
            gateQueues={panels?.crowd?.gate_queues || []}
            parkingLots={parking}
            onOpenIncident={() => navigate("/incidents")}
          />
        </TabsContent>

        <TabsContent value="ai" className="flex-1 m-0 overflow-y-auto">
          <AIIntelligenceTab
            ai={panels?.detection?.ai_intelligence}
            onOpenIncident={() => navigate("/incidents")}
          />
        </TabsContent>

        {TABS.filter((t) => !t.enabled).map(({ key, label, hint, icon: Icon }) => (
          <TabsContent key={key} value={key} className="flex-1 m-0 overflow-y-auto">
            <div className="p-6 flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
              <Icon className="w-8 h-8" />
              <div className="font-display font-bold text-foreground">{label}</div>
              <div className="text-[11px] max-w-md">{hint}</div>
              <div className="micro-label text-[9px]">Coming In Next Build</div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
